
import { AttributesMixin, define, Model, type } from "@type-r/models";
import { ConnectionSettingsDTO } from "./connectionSettings";
import { getAuthorizationBearer, getPlatformCode, restfulIO } from "./endpoint";

export enum EncryptionMethodType {
  Password = 1,
  PrivateKey = 2
}

export interface ConnectionDTO extends AttributesMixin<typeof ConnectionDTO> { }
@define
export class ConnectionDTO extends Model {
  static endpoint = restfulIO('api/v1/ingest/connections');
  static attributes = {
    name: type(String).required,
    flowTypeId: type(Number).required,
    connectionId: type(Number).null,
    customerCompanyId: type(Number).required,
    customerCompanyName: type(String),
    channelTypeId: type(Number).value(1).required,
    sshPrivateKeyFileName: type(String),
    settings: type(ConnectionSettingsDTO),
    isActive: Boolean,
    encryptionMethodId: type(Number).value(1),
    file: type(File).null
  }

  async save() {
    const url = (this.getEndpoint() as any).getUrl(this);
    const httpVerb = this.id ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method: httpVerb,
      headers: new Headers({
        'Authorization': getAuthorizationBearer(),
        'X-Platform-Code': getPlatformCode()
      }),

      body: this.buildConnectionFormData()
    });

    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;
      await response.json()
        .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
        .catch(e => console.error('Cannot parse response data'));
      throw new Error(error);
    }

    const json = await response.json();
    this.set(json);
    return this;
  }

  async get(id: string): Promise<ConnectionDTO> {
    const url = `api/v1/ingest/connections/${id}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: new Headers({
        "Authorization": getAuthorizationBearer(),
        'X-Platform-Code': getPlatformCode()
      }),
    });

    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;
      await response.json()
        .then(e => error = Object.keys(e.errors).map(k => e.errors[k].join(' ')).join(' '))
        .catch(e => console.error('Cannot parse response data'));
      throw new Error(error);
    }

    const json = await response.json();
    this.set(json);
    return this;
  }

  async checkConnection(): Promise<any> {
    const url = `api/v1/ingest/connections/check`;

    const response = await fetch(url, {
      method: 'POST',
      headers: new Headers({
        'Authorization': getAuthorizationBearer(),
        'X-Platform-Code': getPlatformCode()
      }),

      body: this.buildRemoteServerDetailsFormData()
    });

    if (response.status < 200 || response.status >= 300) {
      let error = response.statusText || `HTTP Error ${response.status}`;

      await response.json()
        .then(e => error = e.error)
        .catch(e => console.error('Cannot parse response data'));

      throw new Error(error);
    }

    return await response.json();
  }

  isValidForConnect() {
    let isValid = true;

    //SFTP validation
    if (this.channelTypeId === 1) {
      isValid = this.settings.serverName && this.settings.serverName.length > 0;
      isValid = this.settings.host && this.settings.host.length > 0;
      isValid = Boolean(this.settings.$.userName.value) &&
        this.settings.$.port.value !== 0 &&
        Boolean(this.$.encryptionMethodId.value)

      if (this.$.encryptionMethodId.value === EncryptionMethodType.Password && isValid) {
        isValid = Boolean(this.settings.$.password.value);
      }

      if (this.$.encryptionMethodId.value === EncryptionMethodType.PrivateKey && isValid) {
        isValid = Boolean(this.sshPrivateKeyFileName) || Boolean(this.file);
      }
    }

    //EMAIL validation
    if (this.channelTypeId === 3) {
      const duplicates = this.findDuplicates(this.settings.paths);
      isValid = duplicates.length < 1;
    }

    return isValid;
  }

  findDuplicates = (list: any): any => {
    const duplicates = [];

    list.forEach((p, index) => {
      if (list.slice(index + 1).some((p1) => p1.fileCode === p.fileCode && p1.emailFrom === p.emailFrom)) {
        if (!duplicates.some((d) => d.fileCode === p.fileCode && d.emailFrom === p.emailFrom)) {
          duplicates.push(p);
        }
      }
    });

    return duplicates;
  };

  buildRemoteServerDetailsFormData() {
    const formData = new FormData();
    formData.append('host', this.defaultValue(this.settings.host));
    formData.append('port', this.defaultValue(this.settings.port));
    formData.append('userName', this.defaultValue(this.settings.userName));
    formData.append('password', this.defaultValue(this.settings.password));
    formData.append('customerCompanyId', this.defaultValue(this.customerCompanyId));
    formData.append('encryptionMethodId', this.defaultValue(this.encryptionMethodId));

    if (this.file) {
      formData.append("file", this.file, this.file.name);
    }

    if (this.id) {
      formData.append("connectionId", this.defaultValue(this.id));
    }

    return formData;
  }

  buildConnectionFormData() {
    const formData = new FormData();
    formData.append('name', this.defaultValue(this.name));
    formData.append('channelTypeId', this.defaultValue(this.channelTypeId));
    formData.append('flowTypeId', this.defaultValue(this.flowTypeId));
    formData.append('customerCompanyId', this.defaultValue(this.customerCompanyId));
    formData.append('isActive', this.defaultValue(this.isActive) ? 'True' : 'False');
    formData.append('encryptionMethodId', this.defaultValue(this.encryptionMethodId));
    this.appendFromDataSettings({ formData });

    if (this.id) {
      formData.append("id", this.id);
    }

    if (this.file) {
      formData.append("file", this.file, this.file.name);
    }

    return formData;
  }

  appendFromDataSettings({ formData }: { formData: FormData }) {
    formData.append('settings.serverName', this.defaultValue(this.settings.serverName));
    formData.append('settings.host', this.defaultValue(this.settings.host));
    formData.append('settings.port', this.defaultValue(this.settings.port));
    formData.append('settings.userName', this.defaultValue(this.settings.userName));
    formData.append('settings.password', this.defaultValue(this.settings.password));
    this.appendFromDataPaths({ formData });
  }

  appendFromDataPaths({ formData }: { formData: FormData }) {
    if (!this.settings.paths.length) {
      formData.append('settings.paths', '[]');
    }

    this.settings.paths.forEach((item, index) => {
      formData.append(`settings.paths[${index}].id`, this.defaultValue(item.id));
      formData.append(`settings.paths[${index}].path`, this.defaultValue(item.path));
      formData.append(`settings.paths[${index}].emailFrom`, this.defaultValue(item.emailFrom));
      formData.append(`settings.paths[${index}].fileCode`, this.defaultValue(item.fileCode.replace(' ', '')));
      formData.append(`settings.paths[${index}].useStandardMapping`, this.defaultValue(item.useStandardMapping) ? 'True' : 'False');
      formData.append(`settings.paths[${index}].importTemplateId`, this.defaultValue(item.importTemplateId));
      formData.append(`settings.paths[${index}].frequency`, this.defaultValue(item.frequency));
      formData.append(`settings.paths[${index}].modifyExistingAssets`, this.defaultValue(item.modifyExistingAssets) ? 'True' : 'False');
      formData.append(`settings.paths[${index}].mergeExistingAssets`, this.defaultValue(item.mergeExistingAssets) ? 'True' : 'False');
      item.destinationTargets.forEach((target, indexTarget) => {
        formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].isEnabled`, this.defaultValue(target.isEnabled) ? 'True' : 'False');
        formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].platform`, this.defaultValue(target.platform));
        formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].product`, this.defaultValue(target.product));
        formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].assetTypeId`, this.defaultValue(target.assetTypeId));
        target.facilities.forEach((facility, indexFacility) => {
          formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].facilities[${indexFacility}].id`, this.defaultValue(facility.id));
          formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].facilities[${indexFacility}].name`, this.defaultValue(facility.name));
        });
        if (this.defaultValue(target.program.id) && this.defaultValue(target.program.name)) {
          formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].program.id`, this.defaultValue(target.program.id));
          formData.append(`settings.paths[${index}].destinationTargets[${indexTarget}].program.name`, this.defaultValue(target.program.name));
        }
      });
    });
  }

  defaultValue(value: any) {
    return Boolean(value) ? value.toString() : '';
  }
}
